import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { GroupTypes } from "../../../abstract/controller/enum/GroupTypes";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import { isEmptyString, isNonEmptyString } from "../../../abstract/controller/utils/systemUtils";
import { getWindowLock, setWindowLock } from "../../../abstract/redux/slice/overlaySlice";
import {
  getStatus,
  getStatusId,
  isStatusIdle,
  isStatusPositioned,
  setDrag,
  setIdle,
  setPosition,
} from "../../../abstract/redux/slice/statusSlice";
import {
  handleParameterCreate,
  handleParameterMove,
  handleParameterOutputCreate,
} from "../../../abstract/redux/slice/structureSlice";
import IconPlusCircled from "../../../themes/icons/IconPlusCircled";
import { ButtonDefault } from "../../ui/buttons/ButtonDefault";
import DialogSelect from "../../ui/modal/DialogSelect";
import { DragOverlay } from "../../ui/overlay/DragOverlay";
import styles from "./ParameterPanel.module.scss";
import { showParameterDrop } from "./logic/showParameterDrop";

const PanelContext = createContext();

function ParameterPanel({ node, position, primary, secondary }) {
  const _dispatch = useDispatch();

  const _status = useSelector(getStatus);
  const _idStatus = useSelector(getStatusId);

  const [_showModal, _setShowModal] = useState(false);
  const [_mouseOver, _setMouseOver] = useState(false);

  const _refTarget = useRef(null);

  const _id = nodeUtils.getID(node.current);
  const _idTop = node.top ? nodeUtils.getID(node.top) : null;
  const _idNext = node.next ? nodeUtils.getID(node.next) : null;
  const _idPrevious = node.previous ? nodeUtils.getID(node.previous) : null;

  const _name = nodeUtils.getName(node.current);

  const _isTop = nodeUtils.isParameterTop(node.current);
  const _isGlobalTop = nodeUtils.isParameterTopGlobal(node.current);
  const _isGlobalNested = nodeUtils.isParameterNestedGlobal(node.current);

  //---------------------------------------------------------------------

  const [{ _nodeDrag, _isOver, _canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.PARAMETER,
      collect: (monitor) => ({
        _nodeDrag: monitor.getItem()?.element,
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        return showParameterDrop(
          item.element,
          secondary ? node.top : node.current,
          node.top,
          secondary
        );
      },
      drop(item) {
        _dispatch(setIdle());

        const levels = nodeUtils.compareLevels(
          secondary ? node.top : node.current,
          item.element,
          secondary
        );

        // If parameters are at the same level (or the dragged one is going up), there are no additional restrictions.
        if (levels >= 0) {
          _dispatch(
            handleParameterMove(
              nodeUtils.getID(item.element),
              secondary ? _idTop : _id,
              secondary,
              false
            )
          );
          return;
        }

        // Restrictions for global parameters.
        if (_isGlobalTop || _isGlobalNested) {
          // If the dragged parameter has references, it can be only linked (not moved).
          if (nodeUtils.getIdReferences(item.element) > 0) {
            _dispatch(
              handleParameterMove(
                nodeUtils.getID(item.element),
                secondary ? _idTop : _id,
                secondary,
                true
              )
            );
            // If the dragged parameter is linked, it can be only moved (not linked).
          } else if (nodeUtils.getIdTarget(item.element)) {
            _dispatch(
              handleParameterMove(
                nodeUtils.getID(item.element),
                secondary ? _idTop : _id,
                secondary,
                false
              )
            );
            // If the dragged parameter has no references and is not linked, it can be linked or moved.
          } else {
            _refTarget.current = item.element;
            _dispatch(setWindowLock(true));
            _setShowModal(true);
          }

          return;
        }

        // Function parameters have no additional restrictions.
        _dispatch(
          handleParameterMove(
            nodeUtils.getID(item.element),
            secondary ? _idTop : _id,
            secondary,
            false
          )
        );
      },
    }),
    [_id, _idTop, node, secondary]
  );

  useEffect(() => {
    if (!_canDrop) {
      return;
    }

    if (_isOver) {
      const group = pathUtils.determineGroup(nodeUtils.getID(_nodeDrag), _id);

      if (group === GroupTypes.INTERNAL_FUNCTIONS || group === GroupTypes.INTERNAL_GLOBALS) {
        _dispatch(setPosition({ hidden: true }));
      } else {
        _dispatch(setPosition({ external: true }));
      }
    } else {
      _dispatch(setDrag({ fromPosition: true }));
    }
  }, [_id, _nodeDrag, _isOver, _canDrop, _dispatch]);

  const handleMouseEnter = (e) => {
    e?.stopPropagation();
    _setMouseOver(true);
  };

  const handleMouseLeave = (e) => {
    e?.stopPropagation();
    _setMouseOver(false);
  };

  //---------------------------------------------------------------------

  const _isNodeOver = isStatusPositioned(_status) && _isOver;

  const getPanelHover = () => {
    if (primary) {
      return getPanelHoverPrimary();
    }

    if (secondary) {
      return getPanelHoverSecondary();
    }

    return null;
  };

  const getPanelHoverPrimary = () => {
    if (isStatusIdle(_status) && isNonEmptyString(_name)) {
      return <PanelHover />;
    } else if (_isNodeOver) {
      return <PanelDrag />;
    } else {
      return <PanelEmpty />;
    }
  };

  const getPanelHoverSecondary = () => {
    if (position.isOneBeforeLast) {
      return getPanelHoverSecondaryBeforeLast();
    }
    if (position.isLast) {
      return getPanelHoverSecondaryLast();
    }

    return null;
  };

  const getPanelHoverSecondaryBeforeLast = () => {
    if (isStatusPositioned(_status) && _idNext === _idStatus) {
      return <PanelEmpty />;
    }

    return null;
  };

  const getPanelHoverSecondaryLast = () => {
    if (isStatusPositioned(_status) && _idPrevious === _idStatus) {
      if (_isNodeOver) {
        return <PanelDrag />;
      } else {
        return <PanelEmpty />;
      }
    } else {
      if (_isNodeOver) {
        return <PanelDrag />;
      } else {
        return <PanelHover />;
      }
    }
  };

  //---------------------------------------------------------------------

  return (
    <PanelContext.Provider
      value={{
        primary,
        secondary,
        id: _id,
        idTop: _idTop,
        idPrevious: _idPrevious,
        status: _status,
        idStatus: _idStatus,
        name: _name,
        refTarget: _refTarget,
        isTop: _isTop,
        isGlobalTop: _isGlobalTop,
        isGlobalNested: _isGlobalNested,
        showModal: _showModal,
        setShowModal: _setShowModal,
        isMouseOver: _mouseOver,
        node,
      }}>
      <div
        ref={drop}
        className={styles["panel"]}
        onMouseOver={handleMouseEnter}
        onMouseOut={handleMouseLeave}>
        {getPanelHover()}
      </div>
      <Modal />
    </PanelContext.Provider>
  );
}

function Modal() {
  const _dispatch = useDispatch();

  const { id, idTop, showModal, setShowModal, refTarget, secondary } = useContext(PanelContext);

  if (!showModal) {
    return null;
  }

  const hasReferences = nodeUtils.isParameterReference(refTarget.current);

  const actionMove = () => {
    return {
      text: "move",
      handler: () => {
        _dispatch(
          handleParameterMove(
            nodeUtils.getID(refTarget.current),
            secondary ? idTop : id,
            secondary,
            false
          )
        );
        setShowModal(false);
      },
    };
  };

  const actionLink = () => {
    return {
      text: "link",
      handler: () => {
        _dispatch(
          handleParameterMove(
            nodeUtils.getID(refTarget.current),
            secondary ? idTop : id,
            secondary,
            true
          )
        );
        _dispatch(setWindowLock(false));
        setShowModal(false);
      },
    };
  };

  const actionCancel = () => {
    return {
      text: "cancel",
      handler: () => {
        _dispatch(setWindowLock(false));
        setShowModal(false);
      },
    };
  };

  const actions = [];

  actions.push(actionLink());

  if (!hasReferences) {
    actions.push(actionMove());
  }

  actions.push(actionCancel());

  return (
    <DialogSelect
      textHeader="parameter options"
      textBody="Select one of the available actions."
      actions={actions}
      cancel={() => {
        _dispatch(setWindowLock(false));
        setShowModal(false);
      }}
    />
  );
}

function PanelDrag() {
  return (
    <div className={styles["panel-drag"]}>
      <DragOverlay type={ItemTypes.PARAMETER} isVisible={true} />;
    </div>
  );
}

function PanelHover() {
  const _dispatch = useDispatch();

  const { id, idTop, name, isTop, isGlobalTop, isGlobalNested, isMouseOver, secondary } =
    useContext(PanelContext);

  const _isLocked = useSelector(getWindowLock);

  const handleButtonAddParam = () => {
    setTimeout(async () => {
      _dispatch(handleParameterCreate(secondary ? idTop : id, secondary));
    }, 500);
  };

  const handleButtonAddOutputParam = () => {
    setTimeout(async () => {
      _dispatch(handleParameterOutputCreate(secondary ? idTop : id, secondary));
    }, 500);
  };

  //---------------------------------------------------------------------

  const showIcon = () => {
    return !_isLocked && !isEmptyString(name);
  };

  const getClassIcon = () => {
    return `${styles["container__icon"]} ${isMouseOver ? styles["container__icon--visible"] : ""}`;
  };

  return (
    <div>
      {showIcon() && (
        <div className={styles["container__icon-overlay"]}>
          <div className={getClassIcon()}>
            <IconPlusCircled />
          </div>
        </div>
      )}
      <div className={styles["panel-hover"]}>
        {isGlobalTop || isGlobalNested ? (
          <ButtonDefault text={"add global\nparameter"} handler={handleButtonAddParam} />
        ) : (
          <ButtonDefault text={"add input\nparameter"} handler={handleButtonAddParam} />
        )}
        {!isGlobalTop && !isGlobalNested && isTop && (
          <ButtonDefault text={"add output\nparameter"} handler={handleButtonAddOutputParam} />
        )}
        {isGlobalTop && <div className={styles["panel-button-dummy"]}></div>}
      </div>
    </div>
  );
}

function PanelEmpty() {
  return <div className={styles["panel-empty"]} />;
}

export default ParameterPanel;
